import { Injectable } from '@angular/core';

import * as d3 from 'd3';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { map, concatAll, toArray, filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { BaseMiscDomainService } from '@common/domain/base-misc-domain.service';
import { SelectOption, SelectOptionNumber } from '@ui/components/form/common';
import * as fromPrtgValuesStoreActions from '@apps/prtg/store/values/values.actions';
import { MediaTypes, UtilsService } from '@common/services';
import { SummaryCounter } from '@common/models';
import { groupedCounter } from '@common/operators';

import { HostMiscApiService } from '../api';
import { IAuroraState } from '../../aurora.state';
import { HostFlatNode, HostsFilter } from '../models';
import { HostStatus, PrtgConstants } from '../prtg-constants';
import { HostHistorical, Item, HistoricalData, HistoricalSerie } from '../models/historical';
import { FlatItem } from '../models/historical/flat-item';
import { HttpEvent } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class HostMiscDomainService extends BaseMiscDomainService {
    constructor(
        private hostMiscApiService: HostMiscApiService,
        private store: Store<IAuroraState>
    ) {
        super();
    }

    getHosts(page: number = 1, limit: number = 100): Observable<HostFlatNode[]> {
        const flatNodes: HostFlatNode[] = [];

        const codeStatuses: Set<string> = new Set<string>();
        const statuses: SelectOption[] = [];

        const types: Set<string> = new Set<string>();

        return this.hostMiscApiService.getHosts(page, limit)
            .pipe(
                map((data: any) => {
                    data.items.forEach(item => {
                        if (!codeStatuses.has(item.status)) {
                            codeStatuses.add(item.status);
                            statuses.push(new SelectOptionNumber(parseInt(HostStatus[item.status], 10), item.status));
                        }

                        if (!types.has(item.categoryName)) {
                            types.add(item.categoryName);
                        }

                        flatNodes.push(HostFlatNode.mapperFromPrtg(item));
                    });

                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgHostsStatusesListLoaded({
                            hostsStatuses: statuses
                        })
                    );

                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgHostsTypesListLoaded({
                            hostsTypes: [...types].map((v: string) =>
                                new SelectOption(v, v)
                            )
                        })
                    );

                    // Mise à jour de la pagination dans le store
                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgHostsPaginationLoaded({
                            total: data.total ?? 0,
                            pages: data.pages ?? 1,
                            currentPage: data.currentPage ?? 1,
                            limit: data.limit ?? 100
                        })
                    );

                    return flatNodes;
                })
            );
    }

    getSummaryHostByStatus(): Observable<SummaryCounter<string>[]> {
        return this.getHosts()
            .pipe(
                take(1),
                groupedCounter<HostFlatNode, string>((flatNode: HostFlatNode) =>
                    flatNode.status
                )
            );
    }

    // HistoricalData[]
    getHostHistory(hostId: string,since?:DateTime,until?:DateTime): Observable<any> {
        return this.hostMiscApiService.getHostHistory(hostId,since,until)
            .pipe(
                map(HostHistorical.mapperFromApi),
                concatAll(),
                map(Item.flatten),
                concatAll(),
                map((flatItem: FlatItem) => {
                    return flatItem;
                }),
                toArray(),
                map((flatItems: FlatItem[]) =>
                    Array.from(UtilsService.groupByTyped<number, FlatItem>(flatItems, fi => fi.channelId))
                        .map((record: [number, FlatItem[]]) =>
                            ({ channel: record[0], data: record[1] })
                        )
                ),
                concatAll(),
                filter((record: { channel: number, data: FlatItem[]; }) =>
                    record.data.some((fi: FlatItem) =>
                        fi.rawValue !== null
                    )
                ),
                map((record: { channel: number, data: FlatItem[]; }) => {
                    const unit = record.data.find((fi: FlatItem) => !UtilsService.isNullOrEmpty(fi.unit)).unit;

                    record.data
                        .filter((fi: FlatItem) =>
                            UtilsService.isNullOrEmpty(fi.unit)
                        )
                        .forEach((fi: FlatItem) =>
                            fi.unit = unit
                        );

                    return record.data;
                }),
                map((flatItems: FlatItem[]) =>
                    Array.from(UtilsService.groupByTyped<string, FlatItem>(flatItems, fi => `${fi.unit}|${fi.channelId}`))
                        .map((value: [string, FlatItem[]]) =>
                            ({ unit: value[0].split('|')[0], series: value[1] })
                        )
                ),
                concatAll(),
                map((record: { unit: string, series: FlatItem[] }) => {
                    const historicalData: HistoricalData = new HistoricalData(record.unit);

                    historicalData.historicalSeries = Array.from(UtilsService.groupBy(record.series, s => s.channelId))
                        .map((value: [number, FlatItem[]]) => {
                            const channelId = value[0];
                            const channel = value[1][0].channel;
                            const historicalSerie: HistoricalSerie = new HistoricalSerie(channelId, channel, historicalData.unit);

                            historicalSerie.data = value[1];

                            historicalData.unit = historicalSerie.unit;

                            return historicalSerie;
                        });

                    return historicalData;
                }),
                toArray(),
                map((hd: HistoricalData[]) =>
                    this.sortHistoricalData(hd)
                )
            );
    }

    export(hostsFilter: HostsFilter, format: MediaTypes): Observable<HttpEvent<Blob>> {
        return this.hostMiscApiService.export(hostsFilter, format);
    }

    sortHistoricalData(hd: HistoricalData[]): any {
        return hd.sort((hda, hdb) => hdb.channelId - hda.channelId);
    }
}
